// src/i18n/es/index.ts

import raSpanishMessages from 'ra-language-spanish';
import { raSpanish } from './reactAdmin';
import summary from './summary';
import simulators from './simulators';
import users from './users';
import devices from './devices';
import kioskSimulator from './kioskSimulator';
import alerts from './alerts';
import groups from './groups';
import hotels from './hotels';
import activityMonitor from './activityMonitor';
import activityMonitorMetrics from './activityMonitorMetrics';
import activityMonitorAdditional from './activityMonitorAdditional';
import errorMonitor from './errorMonitor';
import help from './help';
import errorDefinitions from './errorDefinitions';
import events from './events';
import adminOptions from './adminOptions';
import system from './system';
import logs from './logs';
import labels from './labels';
import channels from './channels';
import scope from './scope';
import preferences from './preferences';
import options from './options';
import reservationStates from './reservationStates';
import arrivals from './arrivals';
import reservations from './reservations';
import wall from './wall';
import channelsAnalytics from './channelsAnalytics';
import dispensers from './dispensers';
import durations from './durations';
import payments from './payments';
import hotelsOverview from './hotelsOverview';
import operacion from './operacion';
import liveBoard from './liveBoard';
import notAuthorized from './notAuthorized';
import insightV2 from './insightV2';
import reconciliation from './reconciliation';
import ask from './ask';
import control from './control';
import status from './status';
import benchmark from './benchmark';
import fit from './fit';
import satisfaction from './satisfaction';
import manual from '@/features/manual/i18n/es';
import assistantConnect from '@/features/assistant-connect/i18n/es';
import releaseNotes from '@/features/release-notes/i18n/es';
import developers from '@/features/developers/i18n/es';

/**
 * Spanish translations
 * Import all feature translations and combine them
 */
const es = {
  ...raSpanishMessages,
  
  ra: {
    ...raSpanish,
    action: {
      ...raSpanish.action,
      next: 'Siguiente',
      back: 'Atrás',
      clear_input_value: 'Limpiar',
      export: 'Exportar',
      expand: 'Expandir',
      close: 'Cerrar',
      confirm: 'Confirmar',
      loading: 'Cargando...',
      saving: 'Guardando...',
      menu: 'Menú',
      search: 'Buscar',
    },
    navigation: {
      ...raSpanish.navigation,
      page_rows_per_page: 'Filas por página:',
      skip_nav: 'Ir al contenido principal',
    },
    sort: {
      ...raSpanish.sort,
      ASC: 'Ascendente',
    },
    configurable: {
      ...raSpanish.configurable,
      customize: 'Personalizar',
    },
    message: {
      ...raSpanish.message,
      error: 'Ha ocurrido un error',
      delete_title: 'Eliminar %{name}',
      delete_content: '¿Está seguro de que desea eliminar este elemento?',
      bulk_delete_title: 'Eliminar %{name} |||| Eliminar %{smart_count} %{name}',
      bulk_delete_content: '¿Está seguro de que desea eliminar este %{name}? |||| ¿Está seguro de que desea eliminar estos %{smart_count} elementos?',
    },
    page: {
      ...raSpanish.page,
      error: 'Algo salió mal',
      empty: 'Sin elementos todavía',
      invite: 'Añada uno para comenzar',
    },
    auth: {
      ...raSpanish.auth,
      logging_in: 'Iniciando sesión...',
    },
  },
  
  resources: {
    summary,
    operacion,
    simulators,
    users,
    devices,
    'kiosk-simulator': kioskSimulator,
    'activity-monitor': activityMonitor,
    'error-monitor': errorMonitor,
    alerts,
    hotels,
    groups,
    activityMonitor,
    activityMonitorMetrics,
    activityMonitorAdditional,
    errorMonitor,
    abandonmentAnalysis: errorMonitor.abandonmentAnalysis,
    help,
    errorDefinitions,
    'error-definitions': errorDefinitions,
    events,
    adminOptions,
    'admin-options': adminOptions,
    system,
    logs,
    labels,
    channels,
    reservationStates,
    arrivals,
    reservations,
    channelsAnalytics,
    dispensers,
    durations,
    payments,
    hotelsOverview,
    liveBoard,
    notAuthorized,
    insightV2,
    reconciliation,
    ask,
    control,
    status,
    benchmark,
    fit,
    satisfaction,
  },

  // Wall screen (mockup 02, `/#/panel?hotel=<id>`)
  wall,

  // Multi-hotel scope selector (Decision 6/§1 of the redesign)
  scope,

  // User preferences store + «Mis preferencias» page (Decision 8/§10)
  preferences,

  // In-app user manual (`/manual`, «Ver en el manual» in page headers)
  manual,

  // «Conecte su asistente de IA» (`/assistant`, app-bar entry)
  assistantConnect,
  // In-app release notes («Novedades»)
  releaseNotes,
  // Developers section (/developers): the public API documentation and, for staff, its keys
  developers,

  // «Opciones del hotel» / «Opciones de Roommatik» (mockup 13, tabs 2-3)
  options,
  
  // Common translations used across the app
  common: {
    locale: 'es',
    // Figure withheld below the minimum sample (Tiempos, Canales).
    sample: {
      insufficient: 'Muestra insuficiente (n = %{count})',
    },
    export: {
      inProgress: 'Exportando…',
      failed: 'No se ha podido completar la exportación. Inténtelo de nuevo.',
    },
    exportFiles: {
      arrivals: 'llegadas',
      benchmark: 'comparativa',
      channels: 'canales',
      control: 'control',
      durations: 'tiempos',
      instalaciones: 'instalaciones',
      operacion: 'operacion',
      payments: 'cobros',
      reconciliation: 'conciliacion',
      reservations: 'reservas',
      summary: 'resumen',
    },
    all: 'Todos',
    notAssigned: 'No asignado',
    linked: 'Vinculado',
    registryLinkOptional: 'Opcional. Elija el registro que corresponde; una vez guardado no podrá cambiarse desde aquí.',
    registryLinkLocked: 'Vinculado al registro. El vínculo no puede modificarse desde aquí.',
    never: 'Nunca',
    no_data_title: 'No hay datos disponibles',
    no_data_description: 'Actualmente no hay datos para mostrar.',

    // Generic terminology: the product runs in hotels AND on ships, so user-facing
    // text must avoid "hotel" / "habitación". Use installation / unit instead.
    terms: {
      property: 'Instalación',
      unit: 'Unidad',
      cabin: 'Camarote',
      site: 'Sede',
    },

    actions: {
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      deleting: 'Eliminando…',
      edit: 'Editar',
      create: 'Crear',
      close: 'Cerrar',
      confirm: 'Confirmar',
      back: 'Volver',
      next: 'Siguiente',
      previous: 'Anterior',
      search: 'Buscar',
      filter: 'Filtrar',
      clear: 'Limpiar',
      export: 'Exportar',
      import: 'Importar',
      refresh: 'Actualizar',
      view: 'Ver',
      download: 'Descargar',
      retry: 'Reintentar',

    },
    
    labels: {
      yes: 'Sí',
      no: 'No',
      all: 'Todos',
      none: 'Ninguno',
      loading: 'Cargando...',
      saving: 'Guardando...',
      deleting: 'Eliminando...',
      processing: 'Procesando...',
      success: 'Éxito',
      error: 'Error',
      warning: 'Advertencia',
      info: 'Información',
      metadata: 'Metadatos',
      items_count: '%{count} elemento |||| %{count} elementos',
      devices: 'Dispositivos',
      optional: 'Opcional',
    },
    
    messages: {
      confirm_delete: '¿Está seguro de que desea eliminar este elemento?',
      delete_success: 'Elemento eliminado correctamente',
      delete_error: 'Error al eliminar el elemento',
      save_success: 'Cambios guardados correctamente',
      save_error: 'Error al guardar los cambios',
      required_field: 'Este campo es obligatorio',
      invalid_email: 'Correo electrónico no válido',
      no_results: 'No se encontraron resultados',
      no_data: 'No hay datos disponibles',
      load_error: 'No se ha podido cargar la información. Inténtelo de nuevo.',
    },
    
    notifications: {
      session_extend_error: 'Error al extender la sesión. Por favor, inicie sesión nuevamente.',
      session_extended_with_time: 'Sesión extendida. Nueva expiración: %{time}',
    },

    // Shared period picker (Resumen, Tiempos, Cobros, Hoteles)
    period: {
      from: 'Desde',
      to: 'Hasta',
      compare: 'Comparar con el periodo anterior',
    },
    
    navigation: {
      home: 'Inicio',
      dashboard: 'Panel de Control',
      settings: 'Configuración',
      profile: 'Perfil',
      logout: 'Cerrar Sesión',
      help: 'Ayuda',
    },
    
    filters: {
      label: 'Filtros',
      show: 'Mostrar filtros',
      hide: 'Ocultar filtros',
    },

    help: {
      tooltip: 'Ayuda sobre esta página',
      close: 'Cerrar',
      watchVideo: 'Ver video tutorial',
      features: 'Características Principales',
      keyFeatures: 'Características Clave',
      useCases: 'Casos de Uso',
      tips: 'Consejos y Trucos',
      quickTips: 'Consejos Rápidos',
      ...help,
    },
  },
  
  // Application shell (sidebar groups, environment pill)
  shell: {
    menu: {
      groups: {
        reception: 'Recepción',
        direction: 'Dirección',
        finance: 'Finanzas',
        incidents: 'Incidencias',
        systems: 'Sistemas',
        administration: 'Administración',
        help: 'Ayuda',
      },
    },
    sections: {
      label: 'Secciones',
    },
    environment: {
      nonProduction: 'Pruebas',
    },
    impersonation: {
      active: 'Suplantando a %{name}',
      exit: 'Salir',
    },
    export: {
      button: 'Exportar',
      generating: 'Generando exportación…',
      empty: 'No hay filas para exportar',
      error: 'No se pudo generar la exportación',
    },
    subMenu: {
      scrollLeft: 'Mostrar las pestañas anteriores',
      scrollRight: 'Mostrar más pestañas',
    },
  },

  // Auth related translations
  auth: {
    login: 'Iniciar Sesión',
    logout: 'Cerrar Sesión',
    username: 'Usuario',
    password: 'Contraseña',
    email: 'Correo Electrónico',
    remember_me: 'Recordarme',
    forgot_password: 'Olvidé mi contraseña',
    login_error: 'Error al iniciar sesión',
    invalid_credentials: 'Credenciales inválidas',
    session_expired: 'Su sesión ha expirado',
    unauthorized: 'No autorizado',
    session_expiry: {
      title: 'Sesión Por Expirar',
      message: 'Su sesión está por expirar',
      time_remaining: 'Tiempo restante',
      info: 'Será desconectado automáticamente cuando expire la sesión',
      extend: 'Extender Sesión',
    },
  },
  
  // Validation messages
  validation: {
    required: 'Este campo es obligatorio',
    email: 'Debe ser un correo electrónico válido',
    minLength: 'Debe tener al menos %{min} caracteres',
    maxLength: 'No puede tener más de %{max} caracteres',
    minValue: 'Debe ser al menos %{min}',
    maxValue: 'No puede ser mayor a %{max}',
    pattern: 'Formato no válido',
    unique: 'Este valor ya existe',
  },
  
  // Pages translations
  pages: {
    login: {
      subtitle: 'Inicie sesión para comenzar',
      login_button: 'Iniciar Sesión',
      forgot_password: '¿Olvidaste tu contraseña?',
      or: 'o',
      keycloak_button: 'Acceder con Roommatik Auth',
      keycloak_loading: 'Conectando con Roommatik Auth...',
      keycloak_processing: 'Procesando autenticación...',
      keycloak_error: 'Error al autenticarse con Roommatik Auth',
    },
    forgot_password: {
      title: 'Recuperar Contraseña',
      subtitle: 'Introduce tu email y te enviaremos un enlace para restablecer tu contraseña',
      email_label: 'Correo electrónico',
      submit_button: 'Enviar Enlace',
      sending: 'Enviando...',
      back_to_login: 'Volver al Login',
      email_sent: 'Enlace de recuperación enviado',
      success_message: 'Si el email está registrado, recibirás un enlace para restablecer tu contraseña. Revisa tu bandeja de entrada.',
      error: 'Ha ocurrido un error. Inténtalo de nuevo.',
    },
    reset_password: {
      title: 'Restablecer Contraseña',
      new_password: 'Nueva Contraseña',
      confirm_password: 'Confirmar Contraseña',
      submit_button: 'Restablecer Contraseña',
      resetting: 'Restableciendo...',
      back_to_login: 'Volver al Login',
      success: 'Contraseña restablecida correctamente',
      success_message: 'Tu contraseña ha sido restablecida. Serás redirigido al login en breve.',
      redirecting: 'Redirigiendo al login...',
      error: 'Ha ocurrido un error. Inténtalo de nuevo.',
      no_token: 'No se proporcionó un token de restablecimiento. Usa el enlace de tu email.',
      invalid_token: 'El enlace de restablecimiento no es válido o ha expirado.',
      validation_error: 'No se pudo validar el enlace. Inténtalo de nuevo.',
      validating: 'Validando enlace...',
      passwords_dont_match: 'Las contraseñas no coinciden',
      password_too_short: 'La contraseña debe tener al menos 6 caracteres',
    },
  },

  // Public shareable monitor panel (viewer-facing)
  panel: {
    view: {
      activity: 'Monitor de actividad',
      devices: 'Equipos',
    },
    unavailable: {
      title: 'Este panel no está disponible',
      description: 'El enlace puede haber caducado o haberse revocado. Solicite uno nuevo al administrador.',
    },
  },

  // Shareable panel link management (admin-facing)
  panelLinks: {
    create: 'Crear enlace de panel',
    dialog: {
      title: 'Crear enlace de panel',
      view: 'Vista',
      viewActivity: 'Monitor de actividad',
      viewDevices: 'Equipos',
      scope: 'Ámbito',
      scopeGroup: 'Grupo',
      scopeHotel: 'Instalación',
      selectScope: 'Seleccione un ámbito',
      timeWindow: 'Ventana de tiempo',
      window: {
        '1': 'Última hora',
        '6': 'Últimas 6 horas',
        '12': 'Últimas 12 horas',
        '24': 'Últimas 24 horas',
        '48': 'Últimos 2 días',
        '72': 'Últimos 3 días',
        '168': 'Últimos 7 días',
      },
      layout: 'Disposición',
      layoutGrid: 'Cuadrícula',
      layoutColumns: 'Columnas por equipo',
      showLabels: 'Mostrar etiquetas',
      duration: 'Duración',
      duration24h: 'Temporal (24 horas)',
      durationPermanent: 'Display permanente (revocable)',
      label: 'Nombre (opcional)',
      labelHelp: 'Por ejemplo, «TV recepción»',
      submit: 'Crear enlace',
      cancel: 'Cancelar',
    },
    result: {
      title: 'Enlace creado',
      description: 'Comparta esta dirección o el código QR. No volverá a mostrarse.',
      copy: 'Copiar enlace',
      copied: 'Enlace copiado',
      done: 'Hecho',
    },
    list: {
      title: 'Enlaces activos',
      empty: 'No hay enlaces activos.',
      view: 'Vista',
      scope: 'Ámbito',
      expires: 'Caduca',
      permanent: 'Permanente',
      lastSeen: 'Última vez',
      createdBy: 'Creado por',
      revoke: 'Revocar',
      revoked: 'Enlace revocado',
      regenerateHelp: 'Volver a obtener dirección y QR (la dirección anterior dejará de funcionar)',
      open: 'Abrir',
      never: '—',
    },
    created: 'Enlace de panel creado correctamente',
    regenerated: 'Se ha generado una dirección nueva. La anterior ya no es válida.',
    error: 'No se ha podido completar la operación',
  },
};

export default es;